import config from "@/config";

export type TwilioAvailableNumberRow = {
  phone_number?: string;
  friendly_name?: string;
  region?: string;
  postal_code?: string;
  locality?: string;
  rate_center?: string;
  lata?: string;
  iso_country?: string;
  address_requirements?: string;
  capabilities?: Record<string, boolean>;
};

export type TwilioPagination = {
  page?: number;
  page_size?: number;
  result_count?: number;
  has_next_page?: boolean;
  has_previous_page?: boolean;
};

export type TwilioAvailableNumbersResponse = {
  success?: boolean;
  country_code?: string;
  number_type?: string;
  available_phone_numbers?: TwilioAvailableNumberRow[];
  pagination?: TwilioPagination;
  error?: string;
};

export type TwilioRotateResponse = {
  success?: boolean;
  firestore_document_id?: string;
  twilio_incoming_phone_sid?: string;
  phone_number_e164?: string;
  vapi_phone_number_id?: string;
  error?: string;
  detail?: string;
};

export type OutboundCallerLineResponse = {
  success?: boolean;
  document_id?: string;
  exists?: boolean;
  data?: Record<string, unknown> | null;
  error?: string;
};

async function readHttpError(res: Response): Promise<string> {
  try {
    const j = (await res.json()) as { detail?: unknown };
    const d = j?.detail;
    if (typeof d === "string") return d;
    if (d != null) return JSON.stringify(d);
  } catch {
    /* ignore */
  }
  return `Request failed (${res.status})`;
}

export async function postTwilioAvailablePhoneNumbers(payload: {
  country_code?: string;
  number_type?: string;
  query?: Record<string, unknown>;
  page?: number;
}): Promise<TwilioAvailableNumbersResponse> {
  const body: Record<string, unknown> = {
    country_code: payload.country_code ?? "US",
    number_type: payload.number_type ?? "local",
    query: payload.query ?? {},
    page: payload.page ?? 0,
  };
  const res = await fetch(
    `${config.API_BASE_URL}/api/dashboard/twilio/available-phone-numbers`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error(await readHttpError(res));
  return (await res.json()) as TwilioAvailableNumbersResponse;
}

export async function postTwilioRotateOutboundCaller(payload: {
  iso_country?: string;
  provider_state: string;
  provider_postal_code?: string | null;
  phone_number: string;
  twilio_available_snapshot?: Record<string, unknown> | null;
  friendly_name?: string | null;
}): Promise<TwilioRotateResponse> {
  const res = await fetch(
    `${config.API_BASE_URL}/api/dashboard/twilio/rotate-outbound-caller`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (!res.ok) throw new Error(await readHttpError(res));
  return (await res.json()) as TwilioRotateResponse;
}

export async function getOutboundCallerLine(
  documentId: string,
): Promise<OutboundCallerLineResponse> {
  const enc = encodeURIComponent(documentId);
  const res = await fetch(
    `${config.API_BASE_URL}/api/dashboard/twilio/outbound-caller-line/${enc}`,
  );
  if (!res.ok) throw new Error(await readHttpError(res));
  return (await res.json()) as OutboundCallerLineResponse;
}

export type OutboundCallerLineRow = {
  document_id: string;
  iso_country?: string;
  provider_state?: string;
  provider_postal_code?: string | null;
  phone_number_e164?: string;
  twilio_incoming_phone_sid?: string;
  vapi_phone_number_id?: string;
  updated_at?: string;
  rotation_history?: unknown[];
  [key: string]: unknown;
};

export type OutboundCallerLinesListResponse = {
  success?: boolean;
  lines?: OutboundCallerLineRow[];
  count?: number;
  error?: string;
};

export async function getOutboundCallerLines(
  limit = 300,
): Promise<OutboundCallerLinesListResponse> {
  const res = await fetch(
    `${config.API_BASE_URL}/api/dashboard/twilio/outbound-caller-lines?limit=${encodeURIComponent(String(limit))}`,
  );
  if (!res.ok) throw new Error(await readHttpError(res));
  return (await res.json()) as OutboundCallerLinesListResponse;
}

/** Document id format: ``US_{STATE}_{ZIP5|none}`` (matches backend). */
export function outboundCallerLineDocId(
  isoCountry: string,
  state: string,
  postal: string,
): string {
  const cc = (isoCountry || "US").trim().toUpperCase();
  const st = (state || "").trim().toUpperCase();
  const raw = (postal || "").trim();
  const digits = raw.replace(/\D/g, "");
  let z: string;
  if (digits.length >= 5) z = digits.slice(0, 5);
  else if (digits.length > 0) z = digits.padStart(5, "0");
  else z = "none";
  return `${cc}_${st}_${z}`;
}
